import { useCallback, useEffect, useState, type RefObject } from 'react';

export interface ScrollArrowsState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollByPage: (_direction: -1 | 1) => void;
}

// Sub-pixel tolerance — fractional widths leave a remainder that would
// otherwise keep an arrow visible at the very end of the scroll.
const EPSILON = 1;

/**
 * Tracks whether a horizontal scroller still has content off either edge, so
 * click-to-scroll arrows can be shown only when there is somewhere to go.
 */
export const useScrollArrows = (
  ref: RefObject<HTMLElement | null>
): ScrollArrowsState => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > EPSILON);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - EPSILON);
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    sync();
    el.addEventListener('scroll', sync, { passive: true });
    // Re-check when the card resizes or the stage count changes the content
    // width — either can start or stop the row overflowing.
    const observer = new ResizeObserver(sync);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', sync);
      observer.disconnect();
    };
  }, [ref, sync]);

  const scrollByPage = useCallback(
    (direction: -1 | 1) => {
      const el = ref.current;
      if (!el) return;
      // Scroll by most of a viewport so a partially-cut cell lands fully in view.
      el.scrollBy({
        left: direction * el.clientWidth * 0.8,
        behavior: 'smooth',
      });
    },
    [ref]
  );

  return { canScrollLeft, canScrollRight, scrollByPage };
};
